import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import GenreTaxonomySelects from '@/components/GenreTaxonomySelects';
import MultiSelectDropdown from '@/components/MultiSelectDropdown';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import SongPicker from '@/components/SongPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Coins,
  ShieldCheck,
  Info,
  CheckCircle,
  Clock,
  ArrowsClockwise,
  Plus,
  Trash,
} from '@phosphor-icons/react';

const PLATFORMS = [
  { value: 'spotify', label: 'Spotify playlist', connection: 'spotify' },
  { value: 'youtube', label: 'YouTube playlist', connection: 'google' },
  { value: 'instagram', label: 'Instagram', connection: 'meta' },
  { value: 'facebook', label: 'Facebook Page', connection: 'meta' },
  { value: 'tiktok', label: 'TikTok', connection: 'tiktok' },
  { value: 'x', label: 'X', connection: 'twitter' },
];

const CURATOR_TYPES = [
  { value: 'playlist_curator', label: 'Playlist Curator' },
  { value: 'youtube_channel', label: 'YouTube Channel' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'blog_media', label: 'Blog / Media' },
  { value: 'radio', label: 'Radio / Radio Show' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'record_label', label: 'Record Label' },
  { value: 'ar', label: 'A&R' },
  { value: 'manager', label: 'Manager' },
  { value: 'publisher', label: 'Publisher' },
  { value: 'booker', label: 'Booker / Live' },
  { value: 'sync', label: 'Sync' },
  { value: 'other', label: 'Other' },
];

const ADDITIONAL_CHANNEL_PLATFORMS = [
  { value: 'spotify', label: 'Spotify' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'x', label: 'X' },
  { value: 'threads', label: 'Threads' },
  { value: 'vk', label: 'VK' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'soundcloud', label: 'SoundCloud' },
  { value: 'apple_music', label: 'Apple Music' },
  { value: 'amazon_music', label: 'Amazon Music' },
  { value: 'deezer', label: 'Deezer' },
  { value: 'tidal', label: 'TIDAL' },
  { value: 'beatport', label: 'Beatport' },
  { value: 'yandex_music', label: 'Yandex Music' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'discord', label: 'Discord' },
  { value: 'website', label: 'Website' },
  { value: 'blog', label: 'Blog' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'online_magazine', label: 'Online Magazine' },
  { value: 'radio', label: 'Radio' },
  { value: 'radio_show', label: 'Radio Show' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'other', label: 'Other' },
];

const MUSIC_CHANNEL_PLATFORMS = new Set([
  'spotify',
  'youtube',
  'soundcloud',
  'apple_music',
  'amazon_music',
  'deezer',
  'tidal',
  'beatport',
  'yandex_music',
]);

const SOCIAL_CHANNEL_PLATFORMS = new Set([
  'instagram',
  'facebook',
  'tiktok',
  'x',
  'threads',
  'vk',
  'telegram',
  'reddit',
  'twitch',
  'discord',
]);

const isMusicChannelPlatform = (platform) =>
  MUSIC_CHANNEL_PLATFORMS.has(platform);

const isSocialChannelPlatform = (platform) =>
  SOCIAL_CHANNEL_PLATFORMS.has(platform);

const SONG_STYLES = [
  { value: 'acoustic', label: 'Acoustic' },
  { value: 'ballad', label: 'Ballad' },
  { value: 'beats', label: 'Beats' },
  { value: 'christmas', label: 'Christmas' },
  { value: 'experimental', label: 'Experimental' },
  { value: 'goth', label: 'Goth' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'kids', label: 'Kids' },
  { value: 'traditional', label: 'Traditional' },
  { value: 'none_of_these', label: 'None of these' },
];

const MUSIC_CULTURES = [
  { value: 'african', label: 'African' },
  { value: 'appalachian', label: 'Appalachian' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'asian', label: 'Asian' },
  { value: 'buddhist', label: 'Buddhist' },
  { value: 'caribbean', label: 'Caribbean' },
  { value: 'celtic', label: 'Celtic' },
  { value: 'christian', label: 'Christian' },
  { value: 'hindu', label: 'Hindu' },
  { value: 'indigenous', label: 'Indigenous' },
  { value: 'islamic', label: 'Islamic' },
  { value: 'judaic', label: 'Judaic' },
  { value: 'latin', label: 'Latin' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'sikh', label: 'Sikh' },
  { value: 'south_asian', label: 'South Asian' },
  { value: 'none_of_these', label: 'None of these' },
];

const CURATOR_MOODS = [
  { value: 'chill', label: 'Chill' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'happy', label: 'Happy' },
  { value: 'fierce', label: 'Fierce' },
  { value: 'meditative', label: 'Meditative' },
  { value: 'psychedelic', label: 'Psychedelic' },
  { value: 'romantic', label: 'Romantic' },
  { value: 'sad', label: 'Sad' },
  { value: 'sexy', label: 'Sexy' },
  { value: 'aggressive', label: 'Aggressive' },
  { value: 'noisy', label: 'Noisy' },
  { value: 'calm', label: 'Calm' },
  { value: 'none_of_these', label: 'None of these' },
];

const PRICE_TIERS = [
  ['0–20', 0],
  ['21–100', 1],
  ['101–500', 2],
  ['501–1,000', 3],
  ['1,001–2,000', 4],
  ['2,001–5,000', 5],
  ['5,001–10,000', 6],
  ['10,001–20,000', 7],
  ['20,001–50,000', 8],
  ['50,001–100,000', 9],
  ['100,001–200,000', 10],
  ['200,001–500,000', 11],
  ['500,001+', 12],
];

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : '—';
};

const metricLabel = (verification) => {
  const metric = verification?.pricing_metric;
  if (!metric) return 'Recent audience activity';

  const labels = {
    playlistViews_30d: 'Playlist views · last 30 days',
    playlistStarts_30d: 'Playlist starts · last 30 days',
    median_views_last_5_posts: 'Median views · last 5 posts',
    median_impressions_last_5_posts: 'Median impressions · last 5 posts',
  };

  return labels[metric] || metric.replaceAll('_', ' ');
};

const itemMetricLabel = (platform) => {
  if (platform === 'x') return 'Impressions';
  return 'Views';
};


function MatchingCriteriaFields({
  values,
  onChange,
  prefix = 'criteria',
}) {
  return (
    <div className="space-y-4">

      <GenreTaxonomySelects
        genre={values.genres || values.genre || []}
        subgenre={values.subgenres || values.subgenre || []}
        microgenre={values.microgenres || values.microgenre || []}
        multiple
        prefix={`${prefix}-taxonomy`}
        onChange={(next) =>
          onChange({
            ...values,
            genres: next.genre,
            subgenres: next.subgenre,
            microgenres: next.microgenre,
            genre: next.genre,
            subgenre: next.subgenre?.[0] || null,
            microgenre: next.microgenre?.[0] || null,
          })
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

        <MultiSelectDropdown
          label="Song styles"
          options={SONG_STYLES}
          value={values.song_styles || []}
          onChange={(value) =>
            onChange({
              ...values,
              song_styles: value,
            })
          }
          max={3}
          placeholder="Select styles"
          testid={`${prefix}-song-styles`}
        />

        <MultiSelectDropdown
          label="Music cultures"
          options={MUSIC_CULTURES}
          value={values.music_cultures || []}
          onChange={(value) =>
            onChange({
              ...values,
              music_cultures: value,
            })
          }
          max={3}
          placeholder="Select cultures"
          testid={`${prefix}-music-cultures`}
        />

        <MultiSelectDropdown
          label="Mood / energy"
          options={CURATOR_MOODS}
          value={values.moods || []}
          onChange={(value) =>
            onChange({
              ...values,
              moods: value,
            })
          }
          max={5}
          placeholder="Select moods"
          testid={`${prefix}-moods`}
        />

      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        <Input
          type="number"
          min="0"
          max="300"
          step="1"
          value={values.bpm_min ?? ''}
          onChange={(e) =>
            onChange({
              ...values,
              bpm_min: e.target.value,
            })
          }
          className="bg-zinc-950 border-zinc-800"
          placeholder="BPM min"
        />

        <Input
          type="number"
          min="0"
          max="300"
          step="1"
          value={values.bpm_max ?? ''}
          onChange={(e) =>
            onChange({
              ...values,
              bpm_max: e.target.value,
            })
          }
          className="bg-zinc-950 border-zinc-800"
          placeholder="BPM max"
        />

      </div>

      <Input
        value={values.similar_artists || ''}
        onChange={(e) =>
          onChange({
            ...values,
            similar_artists: e.target.value,
          })
        }
        className="bg-zinc-950 border-zinc-800"
        placeholder="Similar artists, separated by commas"
      />

    </div>
  );
}

export default function CuratorRegister() {
  const { user } = useAuth();
  const [existing, setExisting] = useState(null);
  const [f, setF] = useState({
    playlist_name: '',
    genre: ['indie'],
    subgenre: [],
    microgenre: [],
    subgenres: [],
    microgenres: [],
    moods: [],
    song_styles: [],
    music_cultures: [],
    bpm_min: '',
    bpm_max: '',
    similar_artists: '',
    curator_type: 'playlist_curator',
    platform: 'spotify',
    target_url: '',
    bio: '',
    avatar: '',
    accept_free_credits: false,
    ai_policy: 'allow_all',
  });

  const [channels, setChannels] = useState([]);
  const [sharedChannelCriteria, setSharedChannelCriteria] = useState(true);
  const [channelDraft, setChannelDraft] = useState({
    platform: 'website',
    target_url: '',
    genre: [],
    subgenre: [],
    microgenre: [],
  });

  const createEmptyChannel = () => ({
    platform: 'spotify',
    target_url: '',
    genres: ['indie'],
    subgenres: [],
    microgenres: [],
    moods: [],
    song_styles: [],
    music_cultures: [],
    bpm_min: '',
    bpm_max: '',
    energy_min: null,
    energy_max: null,
    similar_artists: [],
  });

  const [verificationByChannel, setVerificationByChannel] = useState({});

  const getChannelVerificationKey = (channel) =>
    `${channel?.platform || ''}::${String(channel?.target_url || '').trim()}`;
  const [connected, setConnected] = useState(null);
  const [connectionConfirmed, setConnectionConfirmed] = useState(null);
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [songId, setSongId] = useState('');
  const [playlistId, setPlaylistId] = useState('');
  const [contentTypeByChannel, setContentTypeByChannel] = useState({});
  const [playlistLink, setPlaylistLink] = useState('');
  const [addingPlaylist, setAddingPlaylist] = useState(false);
  const [showAddPlaylist, setShowAddPlaylist] = useState(false);
  const [showEditPlaylist, setShowEditPlaylist] = useState(false);
  const [editPlaylistId, setEditPlaylistId] = useState('');
  const [editPlaylistForm, setEditPlaylistForm] = useState({
    name: '',
    source_url: '',
  });
  const [editingPlaylist, setEditingPlaylist] = useState(false);
  const [refreshingPlaylist, setRefreshingPlaylist] = useState(false);
  const [fetchingPlaylistLyrics, setFetchingPlaylistLyrics] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get('/curators/me'),
      api.get('/social/providers'),
    ]).then(([curatorResult, providersResult]) => {
      if (curatorResult.status === 'fulfilled') {
        const data = curatorResult.value.data;
        if (data?.curator_id) {
          setExisting(data);

          setSharedChannelCriteria(
            data.shared_channel_criteria !== false
          );

          setChannels(
            Array.isArray(data.channels) && data.channels.length
              ? data.channels.map((channel) => ({
                  ...createEmptyChannel(),
                  ...channel,
                }))
              : [
                  {
                    ...createEmptyChannel(),
                    platform: data.platform || 'spotify',
                    target_url:
                      data.target_url ||
                      data.spotify_link ||
                      '',
                    genres: Array.isArray(data.genres)
                      ? data.genres
                      : data.genre
                        ? [data.genre]
                        : ['indie'],
                  },
                ]
          );

          setF({
            playlist_name: data.playlist_name || '',
            genre: Array.isArray(data.genres)
              ? data.genres
              : data.genre
                ? [data.genre]
                : ['indie'],
            subgenre: Array.isArray(data.subgenres)
              ? data.subgenres
              : data.subgenre
                ? [data.subgenre]
                : [],
            microgenre: Array.isArray(data.microgenres)
              ? data.microgenres
              : data.microgenre
                ? [data.microgenre]
                : [],
            subgenres: Array.isArray(data.subgenres)
              ? data.subgenres
              : data.subgenre
                ? [data.subgenre]
                : [],
            microgenres: Array.isArray(data.microgenres)
              ? data.microgenres
              : data.microgenre
                ? [data.microgenre]
                : [],
            moods: Array.isArray(data.moods) ? data.moods : [],
            song_styles: Array.isArray(data.song_styles)
              ? data.song_styles
              : [],
            music_cultures: Array.isArray(data.music_cultures)
              ? data.music_cultures
              : [],
            bpm_min: data.bpm_min ?? '',
            bpm_max: data.bpm_max ?? '',
            similar_artists: Array.isArray(data.similar_artists)
              ? data.similar_artists.join(', ')
              : '',
            curator_type: data.curator_type || 'playlist_curator',
            platform: data.platform || 'spotify',
            target_url: data.target_url || data.spotify_link || '',
            bio: data.bio || '',
            avatar: data.avatar || '',
            accept_free_credits: Boolean(data.accept_free_credits),
            ai_policy: data.ai_policy || 'allow_all',
          });

          const verificationMap = {};
          if (Array.isArray(data.channels)) {
            data.channels.forEach((channel) => {
              const key = getChannelVerificationKey(channel);
              if (key !== '::') {
                verificationMap[key] = channel;
              }
            });
          }
          setVerificationByChannel(verificationMap);
        }
      }

      if (providersResult.status === 'fulfilled') {
        const providers = providersResult.value.data?.providers || [];
        setConnected(providers);
      }
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadMusicLibrary = async () => {
      try {
        const [songsRes, playlistsRes] = await Promise.all([
          api.get('/songs'),
          api.get('/playlists'),
        ]);

        if (cancelled) return;

        const nextSongs = songsRes.data || [];
        const nextPlaylists = playlistsRes.data || [];

        setSongs(nextSongs);
        setPlaylists(nextPlaylists);

        if (nextSongs.length) {
          setSongId((prev) => prev || nextSongs[0].song_id);
        }

        if (nextPlaylists.length) {
          setPlaylistId(
            (prev) => prev || nextPlaylists[0].playlist_id
          );
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load music library:', e);
        }
      }
    };

    loadMusicLibrary();

    return () => {
      cancelled = true;
    };
  }, []);

  const platformConfig =
    PLATFORMS.find((item) => item.value === f.platform) || PLATFORMS[0];

  const getPrimaryChannel = () =>
    channels[0] || {
      platform: f.platform || 'spotify',
      target_url: f.target_url || '',
    };

  const primaryChannel = getPrimaryChannel();
  const primaryVerification =
    verificationByChannel[getChannelVerificationKey(primaryChannel)] || null;

  const isConnected =
    f.platform === 'spotify'
      ? (
          connectionConfirmed === true ||
          user?.connections?.spotify?.mode === 'live' ||
          (
            primaryVerification?.platform === 'spotify' &&
            primaryVerification?.verification_status === 'ownership_verified'
          )
        )
      : connected?.some(
          (provider) =>
            provider.id === platformConfig.connection && provider.connected
        );

  const verifyChannel = async (channel) => {
    const platform = channel?.platform;
    const targetUrl = String(channel?.target_url || '').trim();

    if (!platform || !targetUrl) {
      toast.error('Enter the public platform URL first');
      return;
    }

    const verificationKey = getChannelVerificationKey(channel);

    setVerifying(true);
    try {
      const { data } = await api.post('/curators/verify', {
        platform,
        target_url: targetUrl,
      });

      setVerificationByChannel((prev) => ({
        ...prev,
        [verificationKey]: data,
      }));

      if (platform === 'spotify') {
        setConnectionConfirmed(true);
      }

      if (data.pricing_status === 'verified') {
        toast.success('Audience verified automatically.');
      } else {
        toast.message(
          data.note ||
            'Ownership was checked, but a pricing metric is not available yet.'
        );
      }
    } catch (e) {
      setVerificationByChannel((prev) => {
        const next = { ...prev };
        delete next[verificationKey];
        return next;
      });

      if (platform === 'spotify') {
        setConnectionConfirmed(false);
      }

      toast.error(
        e.response?.data?.detail || 'Could not verify this platform account'
      );
    } finally {
      setVerifying(false);
    }
  };

  const reloadMusicLibrary = async () => {
    const [songsRes, playlistsRes] = await Promise.all([
      api.get('/songs'),
      api.get('/playlists'),
    ]);

    const nextSongs = songsRes.data || [];
    const nextPlaylists = playlistsRes.data || [];

    setSongs(nextSongs);
    setPlaylists(nextPlaylists);

    if (
      songId &&
      !nextSongs.some((song) => song.song_id === songId)
    ) {
      setSongId(nextSongs[0]?.song_id || '');
    }

    if (
      playlistId &&
      !nextPlaylists.some(
        (playlist) => playlist.playlist_id === playlistId
      )
    ) {
      setPlaylistId(nextPlaylists[0]?.playlist_id || '');
    }

    if (!songId && nextSongs.length) {
      setSongId(nextSongs[0].song_id);
    }

    if (!playlistId && nextPlaylists.length) {
      setPlaylistId(nextPlaylists[0].playlist_id);
    }

    return {
      songs: nextSongs,
      playlists: nextPlaylists,
    };
  };

  const addPlaylist = async () => {
    if (!playlistLink.trim()) {
      toast.error('Paste a playlist URL');
      return;
    }

    setAddingPlaylist(true);

    try {
      const { data } = await api.post('/playlists/import', {
        url: playlistLink.trim(),
      });

      await reloadMusicLibrary();
      setPlaylistId(data.playlist_id);
      setPlaylistLink('');
      setShowAddPlaylist(false);

      toast.success(`Playlist "${data.name}" added`);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not import playlist'
      );
    } finally {
      setAddingPlaylist(false);
    }
  };

  const openEditPlaylist = (playlist) => {
    setEditPlaylistId(playlist.playlist_id);
    setEditPlaylistForm({
      name: playlist.name || '',
      source_url:
        playlist.source_url ||
        playlist.spotify_url ||
        playlist.youtube_url ||
        '',
    });
    setShowEditPlaylist(true);
  };

  const editPlaylist = async () => {
    if (!editPlaylistId) return;

    if (!editPlaylistForm.name.trim()) {
      toast.error('Playlist name required');
      return;
    }

    setEditingPlaylist(true);

    try {
      await api.patch(`/playlists/${editPlaylistId}`, {
        name: editPlaylistForm.name,
        source_url: editPlaylistForm.source_url?.trim() || null,
      });

      setShowEditPlaylist(false);
      setEditPlaylistId('');
      await reloadMusicLibrary();
      setPlaylistId(editPlaylistId);

      toast.success('Playlist updated');
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not update playlist'
      );
    } finally {
      setEditingPlaylist(false);
    }
  };

  const refreshPlaylist = async (playlist) => {
    if (!playlist) return;

    const confirmed = window.confirm(
      `Refresh "${playlist.name}" from the original playlist source?`
    );

    if (!confirmed) return;

    setRefreshingPlaylist(true);

    try {
      const { data } = await api.post(
        `/playlists/${playlist.playlist_id}/refresh`
      );

      await reloadMusicLibrary();
      setPlaylistId(playlist.playlist_id);

      toast.success(
        `Playlist refreshed: ${data.added_count || 0} added, ${data.removed_count || 0} removed`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not refresh playlist'
      );
    } finally {
      setRefreshingPlaylist(false);
    }
  };

  const getPlaylistLyrics = async (playlist) => {
    if (!playlist) return;

    setFetchingPlaylistLyrics(true);

    try {
      const { data } = await api.post(
        `/playlists/${playlist.playlist_id}/fetch-lyrics`
      );

      await reloadMusicLibrary();
      setPlaylistId(playlist.playlist_id);

      toast.success(
        `Lyrics fetched: ${data.fetched_count || 0} added, ${data.skipped_count || 0} already had lyrics`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not fetch playlist lyrics'
      );
    } finally {
      setFetchingPlaylistLyrics(false);
    }
  };

  const save = async () => {
    const primaryChannel = getPrimaryChannel();

    if (!String(primaryChannel.target_url || '').trim()) {
      toast.error('Platform URL is required');
      return;
    }

    if (!Array.isArray(f.genre) || f.genre.length === 0) {
      toast.error('At least one genre is required');
      return;
    }

    setSaving(true);
    try {
      const bpmMin = f.bpm_min === '' ? null : Number(f.bpm_min);
      const bpmMax = f.bpm_max === '' ? null : Number(f.bpm_max);

      if (
        bpmMin !== null &&
        bpmMax !== null &&
        (Number.isNaN(bpmMin) || Number.isNaN(bpmMax) || bpmMin > bpmMax)
      ) {
        toast.error('BPM range is invalid');
        return;
      }

      const splitCriteria = value =>
        value
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);

      let normalizedChannels = channels.map((channel) => ({
        ...channel,
        genre: Array.isArray(channel.genres)
          ? channel.genres[0] || null
          : Array.isArray(channel.genre)
            ? channel.genre[0] || null
            : channel.genre || null,
        subgenre: Array.isArray(channel.subgenres)
          ? channel.subgenres[0] || null
          : Array.isArray(channel.subgenre)
            ? channel.subgenre[0] || null
            : channel.subgenre || null,
        microgenre: Array.isArray(channel.microgenres)
          ? channel.microgenres[0] || null
          : Array.isArray(channel.microgenre)
            ? channel.microgenre[0] || null
            : channel.microgenre || null,
        genres: Array.isArray(channel.genres)
          ? channel.genres
          : Array.isArray(channel.genre)
            ? channel.genre
            : channel.genre
              ? [channel.genre]
              : [],
        subgenres: Array.isArray(channel.subgenres)
          ? channel.subgenres
          : Array.isArray(channel.subgenre)
            ? channel.subgenre
            : channel.subgenre
              ? [channel.subgenre]
              : [],
        microgenres: Array.isArray(channel.microgenres)
          ? channel.microgenres
          : Array.isArray(channel.microgenre)
            ? channel.microgenre
            : channel.microgenre
              ? [channel.microgenre]
              : [],
      }));

      if (sharedChannelCriteria) {
        normalizedChannels = normalizedChannels.map((channel) => ({
          ...channel,

          genres:
            Array.isArray(channel.genres) && channel.genres.length
              ? channel.genres
              : f.genre,

          subgenres:
            Array.isArray(channel.subgenres) && channel.subgenres.length
              ? channel.subgenres
              : f.subgenre,

          microgenres:
            Array.isArray(channel.microgenres) && channel.microgenres.length
              ? channel.microgenres
              : f.microgenre,

          moods:
            Array.isArray(channel.moods) && channel.moods.length
              ? channel.moods
              : f.moods,

          song_styles:
            Array.isArray(channel.song_styles) && channel.song_styles.length
              ? channel.song_styles
              : f.song_styles,

          music_cultures:
            Array.isArray(channel.music_cultures) &&
            channel.music_cultures.length
              ? channel.music_cultures
              : f.music_cultures,

          bpm_min:
            channel.bpm_min !== '' && channel.bpm_min != null
              ? channel.bpm_min
              : bpmMin,

          bpm_max:
            channel.bpm_max !== '' && channel.bpm_max != null
              ? channel.bpm_max
              : bpmMax,

          similar_artists:
            Array.isArray(channel.similar_artists) &&
            channel.similar_artists.length
              ? channel.similar_artists
              : splitCriteria(f.similar_artists),
        }));
      }

      const primaryChannel = getPrimaryChannel();

      const { data } = await api.post('/curators/register', {
        ...f,
        shared_channel_criteria: sharedChannelCriteria,
        platform: primaryChannel.platform,
        genre: f.genre[0] || null,
        genres: Array.isArray(f.genre) ? f.genre : [],
        target_url: String(primaryChannel.target_url || '').trim(),
        subgenre: f.subgenre[0] || null,
        microgenre: f.microgenre[0] || null,
        subgenres: Array.isArray(f.subgenre) ? f.subgenre : [],
        microgenres: Array.isArray(f.microgenre) ? f.microgenre : [],
        moods: Array.isArray(f.moods) ? f.moods : [],
        song_styles: Array.isArray(f.song_styles) ? f.song_styles : [],
        music_cultures: Array.isArray(f.music_cultures)
          ? f.music_cultures
          : [],
        bpm_min: bpmMin,
        bpm_max: bpmMax,
        energy_min: null,
        energy_max: null,
        similar_artists: splitCriteria(f.similar_artists),
        channels: normalizedChannels,
      });

      setExisting(data);

      const verificationMap = {};
      if (Array.isArray(data.channels)) {
        data.channels.forEach((channel) => {
          const key = getChannelVerificationKey(channel);
          if (key !== '::') {
            verificationMap[key] = channel;
          }
        });
      }
      setVerificationByChannel(verificationMap);

      await api.put('/curators/me/settings', {
        accept_free_credits: Boolean(f.accept_free_credits),
      });

      if (data.pricing_status === 'verified') {
        toast.success('Curator profile verified and listed.');
      } else {
        toast.success(
          'Curator profile saved. It will stay hidden until audience verification provides a pricing metric.'
        );
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not save curator profile'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
          Curator Onboarding
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">
          Become a curator.
        </h1>
        <p className="text-zinc-400 mt-1 text-sm max-w-2xl">
          Connect your platform and enter the public playlist or profile URL.
          Followers, recent activity and the credit price are verified
          automatically. Nothing is entered manually.
        </p>
      </header>

      <div className="space-y-6">
        <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-6 space-y-4">
          <div>
            <div className="font-mono text-[10px] uppercase text-zinc-500 mb-3">
              Platforms
            </div>

            <div className="space-y-4">
              {channels.map((channel, index) => {
                const label =
                  ADDITIONAL_CHANNEL_PLATFORMS.find(
                    (item) => item.value === channel.platform
                  )?.label || channel.platform;

                const verificationKey =
                  getChannelVerificationKey(channel);

                const channelVerification =
                  verificationByChannel[verificationKey] || null;

                const channelPricingVerified =
                  channelVerification?.pricing_status === 'verified';

                const channelRecentItems =
                  channelVerification?.recent_items || [];

                const musicPlatform =
                  isMusicChannelPlatform(channel.platform);

                const socialPlatform =
                  isSocialChannelPlatform(channel.platform);

                const contentKey =
                  `${verificationKey}::${index}`;

                const channelContentType =
                  contentTypeByChannel[contentKey] ||
                  (musicPlatform ? 'playlist' : '');

                const setChannelContentType = (value) =>
                  setContentTypeByChannel((prev) => ({
                    ...prev,
                    [contentKey]: value,
                  }));

                return (
                  <div
                    key={`${channel.platform}-${index}`}
                    className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden"
                  >
                    <div className="grid lg:grid-cols-[minmax(0,1fr)_320px]">
                      <div className="p-5 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <div>
                            <div className="font-display text-lg font-semibold text-zinc-100">
                              {label || 'Platform'}
                            </div>
                            <div className="text-[10px] font-mono uppercase text-zinc-600 mt-1">
                              {musicPlatform
                                ? 'Music platform'
                                : socialPlatform
                                  ? 'Social platform'
                                  : 'Platform'}
                            </div>
                          </div>

                          {channels.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                setChannels((prev) =>
                                  prev.filter(
                                    (_, channelIndex) =>
                                      channelIndex !== index
                                  )
                                )
                              }
                              className="text-zinc-500 hover:text-red-300"
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>

                        <Select
                          value={channel.platform}
                          onValueChange={(value) =>
                            setChannels((prev) =>
                              prev.map((item, channelIndex) =>
                                channelIndex === index
                                  ? {
                                      ...item,
                                      platform: value,
                                      target_url: '',
                                    }
                                  : item
                              )
                            )
                          }
                        >
                          <SelectTrigger className="bg-zinc-900 border-zinc-800">
                            <SelectValue />
                          </SelectTrigger>

                          <SelectContent>
                            {ADDITIONAL_CHANNEL_PLATFORMS.map((item) => (
                              <SelectItem
                                key={item.value}
                                value={item.value}
                              >
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Input
                          value={channel.target_url}
                          onChange={(e) =>
                            setChannels((prev) =>
                              prev.map((item, channelIndex) =>
                                channelIndex === index
                                  ? {
                                      ...item,
                                      target_url: e.target.value,
                                    }
                                  : item
                              )
                            )
                          }
                          className="mt-3 bg-zinc-900 border-zinc-800"
                          placeholder="https://..."
                        />

                        {musicPlatform && (
                          <div className="mt-5">
                            <div className="flex gap-2 mb-4">
                              <Button
                                type="button"
                                onClick={() =>
                                  setChannelContentType('playlist')
                                }
                                className={`rounded-full ${
                                  channelContentType === 'playlist'
                                    ? 'btn-neon'
                                    : 'border border-zinc-800 bg-transparent text-zinc-400'
                                }`}
                              >
                                Playlist
                              </Button>

                              <Button
                                type="button"
                                onClick={() =>
                                  setChannelContentType('song')
                                }
                                className={`rounded-full ${
                                  channelContentType === 'song'
                                    ? 'btn-neon'
                                    : 'border border-zinc-800 bg-transparent text-zinc-400'
                                }`}
                              >
                                Song
                              </Button>
                            </div>

                            {channelContentType === 'song' ? (
                              <div className="flex gap-2 items-center flex-wrap">
                                <SongPicker
                                  songs={songs}
                                  playlists={playlists}
                                  selectedSongId={songId}
                                  onSongChange={setSongId}
                                  playlistId={playlistId}
                                />
                              </div>
                            ) : (
                              <div className="flex gap-2 items-center flex-wrap">
                                <Select
                                  value={playlistId}
                                  onValueChange={setPlaylistId}
                                >
                                  <SelectTrigger className="bg-zinc-950 border-zinc-800 max-w-sm">
                                    <SelectValue
                                      placeholder={
                                        playlists.length
                                          ? 'Pick a playlist'
                                          : 'No playlists yet'
                                      }
                                    />
                                  </SelectTrigger>

                                  <SelectContent>
                                    {playlists.map((playlist) => (
                                      <SelectItem
                                        key={playlist.playlist_id}
                                        value={playlist.playlist_id}
                                      >
                                        {playlist.name} — {playlist.track_count} tracks
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => setShowAddPlaylist(true)}
                                  className="border-zinc-800 hover:border-zinc-600"
                                >
                                  + Add playlist
                                </Button>

                                {playlistId && (
                                  <>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={() => {
                                        const playlist = playlists.find(
                                          (item) =>
                                            item.playlist_id === playlistId
                                        );
                                        if (playlist) {
                                          openEditPlaylist(playlist);
                                        }
                                      }}
                                      className="border-zinc-800 hover:border-[#ccff00]/60"
                                    >
                                      Edit
                                    </Button>

                                    <Button
                                      type="button"
                                      variant="outline"
                                      disabled={refreshingPlaylist}
                                      onClick={() => {
                                        const playlist = playlists.find(
                                          (item) =>
                                            item.playlist_id === playlistId
                                        );
                                        if (playlist) {
                                          refreshPlaylist(playlist);
                                        }
                                      }}
                                      className="border-zinc-800 hover:border-[#ccff00]/60"
                                    >
                                      {refreshingPlaylist
                                        ? 'Refreshing…'
                                        : 'Refresh playlist'}
                                    </Button>

                                    <Button
                                      type="button"
                                      variant="outline"
                                      disabled={fetchingPlaylistLyrics}
                                      onClick={() => {
                                        const playlist = playlists.find(
                                          (item) =>
                                            item.playlist_id === playlistId
                                        );
                                        if (playlist) {
                                          getPlaylistLyrics(playlist);
                                        }
                                      }}
                                      className="border-zinc-800 hover:border-[#ccff00]/60"
                                    >
                                      {fetchingPlaylistLyrics
                                        ? 'Getting lyrics…'
                                        : 'Get Lyrics'}
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {!musicPlatform && (
                          <div className="mt-5">
                            {channelRecentItems.length > 0 && (
                              <div className="border-t border-zinc-800 pt-4">
                                <div className="font-mono text-[10px] uppercase text-zinc-500 mb-3">
                                  Recent activity
                                </div>

                                <div className="space-y-3">
                                  {channelRecentItems
                                    .slice(0, 5)
                                    .map((item, itemIndex) => (
                                      <div
                                        key={
                                          item.id ||
                                          item.url ||
                                          itemIndex
                                        }
                                        className="border-b border-zinc-800 last:border-0 pb-3 last:pb-0"
                                      >
                                        <div className="text-sm text-zinc-200 truncate">
                                          {item.title ||
                                            item.name ||
                                            'Untitled'}
                                        </div>

                                        <div className="text-[10px] font-mono text-zinc-500 mt-1 flex gap-3 flex-wrap">
                                          {item.artist && (
                                            <span>{item.artist}</span>
                                          )}

                                          {item.views !== null &&
                                            item.views !== undefined && (
                                              <span>
                                                {itemMetricLabel(
                                                  channel.platform
                                                )}{' '}
                                                {formatNumber(item.views)}
                                              </span>
                                            )}

                                          {item.likes !== null &&
                                            item.likes !== undefined && (
                                              <span>
                                                Likes{' '}
                                                {formatNumber(item.likes)}
                                              </span>
                                            )}

                                          {item.comments !== null &&
                                            item.comments !== undefined && (
                                              <span>
                                                Comments{' '}
                                                {formatNumber(item.comments)}
                                              </span>
                                            )}

                                          {item.saves !== null &&
                                            item.saves !== undefined && (
                                              <span>
                                                Saves{' '}
                                                {formatNumber(item.saves)}
                                              </span>
                                            )}

                                          {item.shares !== null &&
                                            item.shares !== undefined && (
                                              <span>
                                                Shares{' '}
                                                {formatNumber(item.shares)}
                                              </span>
                                            )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}

                            {!channelRecentItems.length && (
                              <div className="text-xs text-zinc-600">
                                No recent activity data returned by the platform verification.
                              </div>
                            )}
                          </div>
                        )}

                        {!sharedChannelCriteria && (
                          <div className="mt-5 border-t border-zinc-800 pt-5 space-y-4">
                            <GenreTaxonomySelects
                              genre={channel.genres || []}
                              subgenre={channel.subgenres || []}
                              microgenre={channel.microgenres || []}
                              multiple
                              prefix={`channel-${index}`}
                              onChange={(next) =>
                                setChannels((prev) =>
                                  prev.map((item, channelIndex) =>
                                    channelIndex === index
                                      ? {
                                          ...item,
                                          genres: next.genre,
                                          subgenres: next.subgenre,
                                          microgenres: next.microgenre,
                                        }
                                      : item
                                  )
                                )
                              }
                            />

                            <MatchingCriteriaFields
                              values={channel}
                              prefix={`channel-${index}`}
                              onChange={(next) =>
                                setChannels((prev) =>
                                  prev.map((item, channelIndex) =>
                                    channelIndex === index
                                      ? {
                                          ...item,
                                          ...next,
                                        }
                                      : item
                                  )
                                )
                              }
                            />
                          </div>
                        )}
                      </div>

                      <div className="border-t lg:border-t-0 lg:border-l border-zinc-800 p-5 bg-zinc-900/30">
                        <div
                          className={`rounded-xl p-5 ${
                            channelPricingVerified
                              ? 'border border-[#ccff00]/40 bg-[#ccff00]/5'
                              : 'border border-zinc-800 bg-zinc-950/70'
                          }`}
                        >
                          <div className="font-mono text-[10px] uppercase text-zinc-500">
                            Verified Audience
                          </div>

                          <div className="font-display text-5xl font-bold text-[#ccff00] mt-2 flex items-baseline gap-2">
                            <Coins
                              size={28}
                              weight="duotone"
                              className="text-[#ccff00]"
                            />
                            <span>
                              {channelVerification?.credits_cost ?? '—'}
                            </span>
                          </div>

                          <div className="text-xs font-mono text-zinc-400 mt-1">
                            credits per submission
                          </div>

                          {channelVerification?.verified_reach !== null &&
                            channelVerification?.verified_reach !== undefined && (
                              <div className="mt-4 pt-4 border-t border-zinc-800">
                                <div className="text-[10px] font-mono uppercase text-zinc-500">
                                  Verified recent reach
                                </div>
                                <div className="font-display text-2xl mt-1">
                                  {formatNumber(
                                    channelVerification.verified_reach
                                  )}
                                </div>
                                <div className="text-[10px] font-mono text-zinc-500 mt-1">
                                  {metricLabel(channelVerification)}
                                </div>
                              </div>
                            )}

                          {channelVerification && (
                            <div className="mt-4 pt-4 border-t border-zinc-800 grid grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] font-mono uppercase text-zinc-500">
                                  {channelVerification.platform === 'spotify'
                                    ? 'Playlist followers'
                                    : 'Followers'}
                                </div>
                                <div className="font-display text-lg">
                                  {formatNumber(
                                    channelVerification.followers
                                  )}
                                </div>
                              </div>

                              <div>
                                <div className="text-[10px] font-mono uppercase text-zinc-500">
                                  {channelVerification.platform === 'spotify'
                                    ? 'Profile followers'
                                    : 'Subscribers'}
                                </div>
                                <div className="font-display text-lg">
                                  {formatNumber(
                                    channelVerification.platform === 'spotify'
                                      ? channelVerification.profile_followers
                                      : channelVerification.subscribers
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div
                            className={`mt-4 text-xs font-mono flex items-center gap-2 ${
                              channelPricingVerified
                                ? 'text-emerald-300'
                                : 'text-amber-300'
                            }`}
                          >
                            {channelPricingVerified ? (
                              <CheckCircle size={14} />
                            ) : (
                              <Clock size={14} />
                            )}

                            {channelPricingVerified
                              ? 'Recent audience metric verified'
                              : channelVerification?.verification_status ===
                                  'ownership_verified'
                                ? 'Ownership verified; pricing metric is not available yet'
                                : 'Not verified yet'}
                          </div>

                          <div className="mt-3 text-xs text-zinc-500 leading-relaxed">
                            {channelPricingVerified
                              ? 'Verified using recent public audience data.'
                              : channelVerification?.verification_status ===
                                  'ownership_verified_activity_metric_missing'
                                ? 'Ownership verified; audience metric unavailable.'
                                : 'Verification pending.'}
                          </div>

                          <Button
                            type="button"
                            onClick={() => verifyChannel(channel)}
                            disabled={
                              verifying ||
                              !String(channel.target_url || '').trim()
                            }
                            className="mt-4 w-full btn-neon rounded-full"
                          >
                            {verifying
                              ? 'Verifying…'
                              : channelPricingVerified
                                ? 'Verify again'
                                : 'Verify audience'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                className="rounded-md border-zinc-700"
                onClick={() =>
                  setChannels((prev) => [
                    ...prev,
                    createEmptyChannel(),
                  ])
                }
              >
                <Plus size={15} className="mr-2" />
                Add platform
              </Button>
            </div>

            <Dialog
              open={showAddPlaylist}
              onOpenChange={setShowAddPlaylist}
            >
              <DialogContent className="bg-zinc-950 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="font-display">
                    Add a playlist
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                  <Input
                    placeholder="Paste a Spotify, YouTube, Yandex Music or other playlist link…"
                    value={playlistLink}
                    onChange={(e) => setPlaylistLink(e.target.value)}
                    className="bg-zinc-950 border-zinc-800"
                  />

                  <div className="text-xs text-zinc-500">
                    Example: https://open.spotify.com/playlist/...,
                    https://youtube.com/playlist?list=...
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddPlaylist(false)}
                    className="border-zinc-800"
                  >
                    Cancel
                  </Button>

                  <Button
                    type="button"
                    onClick={addPlaylist}
                    disabled={
                      addingPlaylist || !playlistLink.trim()
                    }
                    className="btn-neon rounded-full"
                  >
                    {addingPlaylist ? 'Adding…' : 'Add playlist'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              open={showEditPlaylist}
              onOpenChange={setShowEditPlaylist}
            >
              <DialogContent className="bg-zinc-950 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="font-display">
                    Edit playlist
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                  <Input
                    placeholder="Playlist name"
                    value={editPlaylistForm.name}
                    onChange={(e) =>
                      setEditPlaylistForm({
                        ...editPlaylistForm,
                        name: e.target.value,
                      })
                    }
                    className="bg-zinc-950 border-zinc-800"
                  />

                  <Input
                    placeholder="Source Link"
                    value={editPlaylistForm.source_url || ''}
                    onChange={(e) =>
                      setEditPlaylistForm({
                        ...editPlaylistForm,
                        source_url: e.target.value,
                      })
                    }
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowEditPlaylist(false)}
                    className="border-zinc-800"
                  >
                    Cancel
                  </Button>

                  <Button
                    type="button"
                    onClick={editPlaylist}
                    disabled={editingPlaylist}
                    className="btn-neon rounded-full"
                  >
                    {editingPlaylist ? 'Saving…' : 'Save changes'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={sharedChannelCriteria}
                  onCheckedChange={(checked) =>
                    setSharedChannelCriteria(checked === true)
                  }
                />

                <span className="text-sm text-zinc-200">
                  Use same genre & matching criteria for all platforms
                </span>
              </div>

              <div className="mt-2 text-[10px] font-mono text-zinc-600">
                When enabled, global matching criteria are applied to every
                platform.
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-zinc-800">
            <div className="font-mono text-[10px] uppercase text-zinc-500 mb-3">
              Matching criteria
            </div>

            <MatchingCriteriaFields
              values={f}
              prefix="curator-global"
              onChange={(next) =>
                setF((prev) => ({
                  ...prev,
                  ...next,
                }))
              }
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase text-zinc-500">
              Bio
            </label>
            <Textarea
              data-testid="curator-bio-input"
              value={f.bio}
              onChange={(e) => setF({ ...f, bio: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 min-h-[100px]"
              placeholder="What kind of music fits your playlist or audience?"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase text-zinc-500">
              Avatar URL (optional)
            </label>
            <Input
              data-testid="curator-avatar-input"
              value={f.avatar}
              onChange={(e) => setF({ ...f, avatar: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800"
              placeholder="https://…"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase text-zinc-500">
              AI music policy
            </label>
            <Select
              value={f.ai_policy}
              onValueChange={(value) =>
                setF((prev) => ({
                  ...prev,
                  ai_policy: value,
                }))
              }
            >
              <SelectTrigger
                data-testid="curator-ai-policy-select"
                className="mt-1 bg-zinc-950 border-zinc-800"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow_all">
                  Allow all AI music
                </SelectItem>
                <SelectItem value="allow_ai_assisted">
                  Allow AI-assisted music
                </SelectItem>
                <SelectItem value="allow_human_only">
                  Human-made music only
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="mt-1 text-[10px] font-mono text-zinc-500">
              Choose what type of AI involvement is acceptable for submissions
              to this curator.
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="curator-accept-free-credits"
                checked={Boolean(f.accept_free_credits)}
                onCheckedChange={(checked) =>
                  setF((prev) => ({
                    ...prev,
                    accept_free_credits: checked === true,
                  }))
                }
              />
              <div>
                <label
                  htmlFor="curator-accept-free-credits"
                  className="text-sm text-zinc-200 cursor-pointer"
                >
                  Accept free-credit submissions
                </label>
                <div className="mt-1 text-[10px] font-mono text-zinc-500">
                  If enabled, artists can submit for free when your verified
                  audience qualifies for the free tier. You receive no credits
                  for those submissions, and the transaction remains recorded.
                </div>
              </div>
            </div>
          </div>

          <Button
            data-testid="curator-save-btn"
            onClick={save}
            disabled={saving}
            className="btn-neon rounded-full font-display w-full h-11"
          >
            {saving
              ? 'Saving…'
              : existing
                ? 'Update profile'
                : 'Register as curator'}
          </Button>
        </div>


      </div>
    </div>
  );
}
